import {
  ComboBox,
  Form,
  InnerForm,
  Modal,
  type TFormMethods,
} from "@fluidattacks/design";
import isEmpty from "lodash/isEmpty";
import { Fragment, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { AddPriorityCriteria } from "./priority-array";

import type { ICriteriaProps, IPolicyCriteria } from "../types";
import { validationSchema } from "../validations";

const AddCriteriaModal = ({
  modalRef,
  loading,
  onSubmit,
}: ICriteriaProps): JSX.Element => {
  const { t } = useTranslation();
  const { close } = modalRef;

  const prefix = "organization.tabs.policies.priority.";
  const resetForm = useRef<TFormMethods<IPolicyCriteria>["reset"]>();

  const handleClose = useCallback((): void => {
    resetForm.current?.();
    close();
  }, [close]);

  return (
    <Modal
      modalRef={{ ...modalRef, close: handleClose }}
      size={"md"}
      title={t(`${prefix}modal.title`)}
    >
      <Form
        cancelButton={{ onClick: handleClose }}
        confirmButton={{ disabled: loading }}
        defaultValues={{
          criteria: [{ policy: "", value: 0 }],
          optionsCriteria: "",
        }}
        onSubmit={onSubmit}
        yupSchema={validationSchema}
      >
        <InnerForm<IPolicyCriteria>>
          {({ setValue, watch, control, register }): JSX.Element => {
            const [optionsCriteria] = watch(["optionsCriteria"]);

            return (
              <Fragment>
                <ComboBox
                  control={control}
                  items={[
                    {
                      name: t(`${prefix}criteria.options.attack`),
                      value: "ATTACK_VECTOR",
                    },
                    {
                      name: t(`${prefix}criteria.options.availability`),
                      value: "AVAILABILITY",
                    },
                    {
                      name: t(`${prefix}criteria.options.confidentiality`),
                      value: "CONFIDENTIALITY",
                    },
                    {
                      name: t(`${prefix}criteria.options.exploitability`),
                      value: "EXPLOITABILITY",
                    },
                    {
                      name: t(`${prefix}criteria.options.integrity`),
                      value: "INTEGRITY",
                    },
                    {
                      name: t(`${prefix}criteria.options.reachability`),
                      value: "REACHABILITY",
                    },
                    {
                      name: t(`${prefix}criteria.options.technique`),
                      value: "TECHNIQUE",
                    },
                  ]}
                  label={t(`${prefix}criteria.text`)}
                  name={"optionsCriteria"}
                  placeholder={t(`${prefix}criteria.placeholder`)}
                />
                {isEmpty(optionsCriteria) ? undefined : (
                  <AddPriorityCriteria
                    control={control}
                    criteriaName={optionsCriteria}
                    register={register}
                    setValue={setValue}
                    watch={watch}
                  />
                )}
              </Fragment>
            );
          }}
        </InnerForm>
      </Form>
    </Modal>
  );
};

export { AddCriteriaModal };
